import { RevenueChart } from "@/components/dashboard/revenue-chart";
import type { YearStat } from "@/components/dashboard/revenue-chart";
import { motion } from "framer-motion";
import { ComposedChart, LineChart, Line, Bar, XAxis, YAxis, CartesianGrid, ResponsiveContainer, Tooltip, ReferenceLine } from "recharts";
import { useState, useEffect, useMemo, useRef } from "react";
import { eachDayOfInterval, isWeekend } from "date-fns";
import { getHolidays } from "@/lib/holidays";
import { Link } from "react-router-dom";
import { Clock, Target, Lock, CheckCircle2 } from "lucide-react";
import { useTheme } from "@/App";
import { getCurrencySymbol } from "@/lib/utils";
import { useNavigate } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/components/AuthProvider";
import { useBookings } from "@/hooks/useBookings";
import { useSubscription } from "@/hooks/useSubscription";

// ─── Tax reserve estimate ─────────────────────────────────────────────────────
// Rough effective-rate lookup by country + income, used as a smart default when
// no saved tax_reserve_pct exists. Values are intentionally conservative.

function estimateTaxReservePct(country: string, entityType: string, ytd: number): number {
  const isCorp = /corp/i.test(entityType);
  const g = ytd > 0 ? ytd : 60_000; // mid-range fallback when no earnings yet
  switch (country) {
    case 'US':
      if (isCorp) return g < 100_000 ? 21 : 25;
      return g < 20_000 ? 18 : g < 50_000 ? 24 : g < 100_000 ? 28 : g < 200_000 ? 33 : 38;
    case 'CA':
      if (isCorp) return g < 100_000 ? 18 : 22;
      return g < 20_000 ? 15 : g < 50_000 ? 22 : g < 100_000 ? 28 : 33;
    case 'UK':
      if (isCorp) return g < 50_000 ? 19 : 25;
      return g < 12_570 ? 10 : g < 50_270 ? 22 : g < 125_140 ? 42 : 45;
    case 'FR':
      return g < 30_000 ? 28 : g < 70_000 ? 33 : 38;
    case 'DE':
      return g < 11_604 ? 14 : g < 30_000 ? 22 : g < 60_000 ? 30 : 38;
    default:
      return 30;
  }
}

// ─── Constants ────────────────────────────────────────────────────────────────

const CLIENT_COLORS = ["#7B61FF", "#93c5fd", "#A259FF", "#4A6CF7", "#94a3b8", "#f59e0b", "#10b981"];

// ─── Helpers ──────────────────────────────────────────────────────────────────

function parseEarnings(earningsStr: string): number {
  return parseFloat(earningsStr.replace(/[^0-9.]/g, "")) || 0;
}

// Converts stored day count to 8-hour-equivalent days.
// For hourly bookings we derive actual total hours from earnings ÷ hourly rate
// (the only reliable source when advanced per-day hour overrides were used), then
// divide by 8 to get standard-day equivalents.
// Fallback to stored hoursPerDay when the rate is unavailable.
function effectiveDays(b: { rateType: 'daily' | 'hourly'; hoursPerDay?: number; days: number; rate: string; earnings: string }): number {
  if (b.rateType === 'hourly') {
    const rateNum = parseFloat(b.rate.replace(/[^0-9.]/g, '')) || 0;
    if (rateNum > 0) return parseEarnings(b.earnings) / rateNum / 8;
    return ((b.hoursPerDay || 8) * b.days) / 8;
  }
  return b.days;
}

// Per-booking scale factor used when attributing booked days to a calendar window.
// Returns the ratio of average hours/day to a standard 8-hour day.
// Uses the same earnings-derived approach as effectiveDays so advanced per-day
// hour overrides are correctly reflected in the chart lines.
function hourlyScale(b: { rateType: 'daily' | 'hourly'; hoursPerDay?: number; days: number; rate: string; earnings: string }): number {
  if (b.rateType !== 'hourly') return 1;
  const rateNum = parseFloat(b.rate.replace(/[^0-9.]/g, '')) || 0;
  if (rateNum > 0 && b.days > 0) {
    const totalHours = parseEarnings(b.earnings) / rateNum;
    return (totalHours / b.days) / 8;
  }
  return (b.hoursPerDay || 8) / 8;
}

function countWeekdaysBetween(start: Date, end: Date): number {
  if (end < start) return 0;
  return eachDayOfInterval({ start, end }).filter(d => !isWeekend(d)).length;
}

// Counts weekdays in [start, end] that are NOT public holidays for the user's calendar.
function countBillableDays(start: Date, end: Date, holidays: Set<string>): number {
  if (end < start) return 0;
  const p2 = (n: number) => String(n).padStart(2, '0');
  return eachDayOfInterval({ start, end }).filter(d => {
    if (isWeekend(d)) return false;
    return !holidays.has(`${d.getFullYear()}-${p2(d.getMonth()+1)}-${p2(d.getDate())}`);
  }).length;
}

// Smooth monotone bezier path through [x,y] points — horizontal tangents at each knot
// so the curve never overshoots, matching Recharts' "monotone" interpolation.
function smoothBezierPath(pts: [number, number][]): string {
  if (pts.length === 0) return '';
  if (pts.length === 1) return `M ${pts[0][0]},${pts[0][1]}`;
  let d = `M ${pts[0][0]},${pts[0][1]}`;
  for (let i = 1; i < pts.length; i++) {
    const [x0, y0] = pts[i - 1];
    const [x1, y1] = pts[i];
    const cpx = (x0 + x1) / 2;
    d += ` C ${cpx},${y0} ${cpx},${y1} ${x1},${y1}`;
  }
  return d;
}

// ─── Component ────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const { isDark, toggleTheme, currency, holidayCalendar } = useTheme();
  const sym = getCurrencySymbol(currency);
  const navigate = useNavigate();
  const { user } = useAuth();
  const { bookings, loading: bookingsLoading } = useBookings();
  const { plan, status } = useSubscription();
  // Junior active subscribers see the Lead gate overlay instead of page content
  const showLeadGate = plan === 'junior' && status === 'active';
  const [selectedYear, setSelectedYear] = useState(String(new Date().getFullYear()));
  const [yearDropdownOpen, setYearDropdownOpen] = useState(false);
  const [yearlyPerformance, setYearlyPerformance] = useState<Record<string, YearStat>>({});
  const [clientsYear, setClientsYear] = useState<string>(String(new Date().getFullYear()));
  const [clientsYearDropdownOpen, setClientsYearDropdownOpen] = useState(false);
  const [avgBookingYear, setAvgBookingYear] = useState<string>(String(new Date().getFullYear()));
  const [avgBookingYearDropdownOpen, setAvgBookingYearDropdownOpen] = useState(false);

  // Once bookings load, set smart defaults for client charts:
  // current year if it has data, OVERALL otherwise
  const chartDefaultsSetRef = useRef(false);
  useEffect(() => {
    if (chartDefaultsSetRef.current || bookings.length === 0) return;
    chartDefaultsSetRef.current = true;
    const currentYr = String(new Date().getFullYear());
    const hasCY = bookings.some(b =>
      b.startDate && String(new Date(b.startDate + "T00:00:00").getFullYear()) === currentYr
    );
    if (!hasCY) {
      setClientsYear("OVERALL");
      setAvgBookingYear("OVERALL");
    }
  }, [bookings]);

  // Tax reserve slider — loaded from DB, or computed from country + live YTD earnings
  const [taxReservePct, setTaxReservePct] = useState(30);
  const [taxPctFromDb, setTaxPctFromDb] = useState<number | null>(null);
  const [taxCountry, setTaxCountry] = useState('');
  const [taxEntityType, setTaxEntityType] = useState('');
  const taxDefaultSetRef = useRef(false);

  useEffect(() => {
    if (!user) return;
    Promise.all([
      supabase.from('users').select('tax_reserve_pct').eq('id', user.id).maybeSingle(),
      supabase.from('app_settings').select('tax_inputs').eq('user_id', user.id).maybeSingle(),
    ]).then(([usersRes, settingsRes]) => {
      const pct = Number(usersRes.data?.tax_reserve_pct) || 0;
      setTaxPctFromDb(pct);
      if (pct > 0) setTaxReservePct(pct);
      const inputs = settingsRes.data?.tax_inputs as Record<string, string> | null;
      if (inputs?.country) setTaxCountry(String(inputs.country));
      if (inputs?.entityType) setTaxEntityType(String(inputs.entityType));
    });
  }, [user]);

  // If DB has no saved value, compute a smart default from country + live YTD earnings
  useEffect(() => {
    if (taxDefaultSetRef.current) return;
    if (taxPctFromDb === null || taxPctFromDb > 0 || bookingsLoading) return;
    taxDefaultSetRef.current = true;
    const now = new Date();
    const currentYear = now.getFullYear();
    const ytd = bookings
      .filter(b => b.startDate &&
        new Date(b.startDate + 'T00:00:00').getFullYear() === currentYear &&
        new Date(b.startDate + 'T00:00:00') <= now)
      .reduce((sum, b) => sum + parseEarnings(b.earnings), 0);
    setTaxReservePct(estimateTaxReservePct(taxCountry, taxEntityType, ytd));
  }, [taxPctFromDb, bookingsLoading, bookings, taxCountry, taxEntityType]);

  // Annual goal for "Against Annual Goal" comparison metric
  const [annualGoal, setAnnualGoal] = useState(0);
  useEffect(() => {
    if (!user) return;
    supabase.from('users').select('annual_goal').eq('id', user.id).maybeSingle()
      .then(({ data }) => { if (data?.annual_goal) setAnnualGoal(Number(data.annual_goal)); });
  }, [user]);

  // Load yearly_performance for RevenueChart
  useEffect(() => {
    if (!user) return;
    supabase
      .from('yearly_performance')
      .select('year, total_earned, total_days_worked, avg_day_rate')
      .eq('user_id', user.id)
      .eq('archived', false)
      .then(({ data }) => {
        if (!data || data.length === 0) return;
        const record: Record<string, YearStat> = {};
        data.forEach(row => {
          const monthlyAvg = Math.round(row.total_earned / 12);
          record[String(row.year)] = {
            earned: `$${Math.round(row.total_earned).toLocaleString('en-US')}`,
            billedDays: Number(row.total_days_worked).toFixed(2),
            dayRate: row.avg_day_rate ? `$${Math.round(row.avg_day_rate).toLocaleString('en-US')}` : '—',
            total: `$${monthlyAvg.toLocaleString('en-US')}`,
          };
        });
        setYearlyPerformance(record);
      });
  }, [user]);

  // Derive available years from bookings + yearly_performance + current year
  const statYears = useMemo(() => {
    const years = new Set<string>();
    years.add(String(new Date().getFullYear()));
    bookings.forEach(b => {
      if (b.startDate) years.add(String(new Date(b.startDate + "T00:00:00").getFullYear()));
    });
    Object.keys(yearlyPerformance).forEach(y => years.add(y));
    return Array.from(years).sort((a, b) => parseInt(b) - parseInt(a));
  }, [bookings, yearlyPerformance]);

  // Keep selectedYear valid when year list changes
  useEffect(() => {
    if (statYears.length > 0 && !statYears.includes(selectedYear)) {
      setSelectedYear(statYears[0]);
    }
  }, [statYears, selectedYear]);

  // Bookings for the selected year
  const yearBookings = useMemo(() => {
    return bookings.filter(b => {
      if (!b.startDate) return false;
      return String(new Date(b.startDate + "T00:00:00").getFullYear()) === selectedYear;
    });
  }, [bookings, selectedYear]);

  // Billable day totals for the selected year (weekdays excl. US federal holidays).
  // Used as the shared denominator for hired %, days remaining, and chart lines.
  const billableStats = useMemo(() => {
    const yr = parseInt(selectedYear);
    const now = new Date();
    const isCurrentYear = yr === now.getFullYear();
    const holidays = getHolidays(yr, holidayCalendar);
    const startOfYear = new Date(yr, 0, 1);
    const endOfYear   = new Date(yr, 11, 31);
    const totalBillableDays  = countBillableDays(startOfYear, endOfYear, holidays);
    const billableDaysPassed = isCurrentYear
      ? countBillableDays(startOfYear, now, holidays)
      : totalBillableDays; // past year is fully elapsed
    const billableDaysRemaining = Math.max(0, totalBillableDays - billableDaysPassed);
    return { holidays, totalBillableDays, billableDaysPassed, billableDaysRemaining };
  }, [selectedYear, holidayCalendar]);

  // Computed metrics for the selected year
  const computedStats = useMemo(() => {
    const now = new Date();
    const yr = parseInt(selectedYear);
    const isCurrentYear = yr === now.getFullYear();

    // Billable days remaining (always computed from billableStats, not bookings)
    const billableDaysRemaining = billableStats.billableDaysRemaining;
    const yearRemainingPct = billableStats.totalBillableDays > 0
      ? Math.max(0, Math.round((billableDaysRemaining / billableStats.totalBillableDays) * 100))
      : 0;

    // Fallback: if no individual bookings exist for the selected year but yearly_performance
    // has a summary row (e.g. user entered annual totals at onboarding), use those summary values.
    const perfRow = yearBookings.length === 0 ? yearlyPerformance[selectedYear] : undefined;
    if (perfRow) {
      const totalEarnedNum = parseEarnings(perfRow.earned);
      const monthlyAvgNum = Math.round(totalEarnedNum / 12);
      const daysWorkedNum = parseFloat(perfRow.billedDays) || 0;
      return {
        hiredPct: "—",
        bookedDays: daysWorkedNum > 0 ? (Number.isInteger(daysWorkedNum) ? String(daysWorkedNum) : daysWorkedNum.toFixed(1)) : "—",
        billableDaysRemaining: `${billableDaysRemaining} of ${billableStats.totalBillableDays}`,
        billableYearRemaining: `${yearRemainingPct}%`,
        monthlyAvg: monthlyAvgNum > 0 ? `$${monthlyAvgNum.toLocaleString('en-US')}` : "—",
        projectedYTD: totalEarnedNum > 0 ? `$${Math.round(totalEarnedNum).toLocaleString('en-US')}` : "—",
        longestBooking: "—",
        longestBookingCompany: "—",
        monthlyIfStopped: monthlyAvgNum > 0 ? `$${monthlyAvgNum.toLocaleString('en-US')}` : "—",
        totalAverage: monthlyAvgNum > 0 ? `$${monthlyAvgNum.toLocaleString('en-US')}` : "—",
        isSummaryFallback: true,
        bookedDaysFallback: daysWorkedNum > 0,
      };
    }

    // For current year, activity metrics (days worked, hired %) use only bookings
    // that have already started — future contracted work hasn't been worked yet.
    // Earnings metrics use ALL contracted bookings (full revenue picture for the year).
    const startedBookings = isCurrentYear
      ? yearBookings.filter(b => new Date(b.startDate + "T00:00:00") <= now)
      : yearBookings;

    const totalEarned = yearBookings.reduce((s, b) => s + parseEarnings(b.earnings), 0);
    const workedDays = startedBookings.reduce((s, b) => s + effectiveDays(b), 0);
    const allContractedDays = yearBookings.reduce((s, b) => s + effectiveDays(b), 0);

    const longestEntry = yearBookings.reduce(
      (max, b) => b.days > max.days ? b : max,
      { days: 0, company: '—' } as { days: number; company: string }
    );

    // Earnings for started bookings only (used for monthly-if-stopped and monthly avg)
    const startedEarnings = startedBookings.reduce((s, b) => s + parseEarnings(b.earnings), 0);

    const monthsElapsed = isCurrentYear ? now.getMonth() + 1 : 12;
    const monthlyAvg = monthsElapsed > 0 ? (isCurrentYear ? startedEarnings : totalEarned) / monthsElapsed : 0;

    // Projected EOY uses working-day ratio (weekdays excl. US holidays) rather than
    // calendar days so weekends don't dilute the denominator. billableStats already
    // provides billableDaysPassed / totalBillableDays for the selected year.
    // Past years: fraction is 1 (year complete), so projectedYTD === totalEarned.
    const projectedYTD = isCurrentYear && billableStats.billableDaysPassed > 0
      ? totalEarned / (billableStats.billableDaysPassed / billableStats.totalBillableDays)
      : totalEarned;

    // Hired % = utilization efficiency: worked days ÷ billable days elapsed (excl. weekends + US holidays)
    const workingDaysElapsed = billableStats.billableDaysPassed;
    const hiredPct = workingDaysElapsed > 0 ? (workedDays / workingDaysElapsed) * 100 : 0;

    // Monthly if stopped = total earned so far ÷ 12 (annual spread, not per-month rate)
    const monthlyIfStopped = isCurrentYear
      ? (startedEarnings > 0 ? Math.round(startedEarnings / 12) : 0)
      : (totalEarned > 0 ? Math.round(totalEarned / 12) : 0);

    return {
      hiredPct: workedDays > 0 ? `${hiredPct.toFixed(1)}%` : "—",
      bookedDays: allContractedDays > 0 ? String(Math.round(allContractedDays)) : "—",
      billableDaysRemaining: `${billableDaysRemaining} of ${billableStats.totalBillableDays}`,
      billableYearRemaining: `${yearRemainingPct}%`,
      monthlyAvg: monthlyAvg > 0 ? `$${Math.round(monthlyAvg).toLocaleString('en-US')}` : "—",
      projectedYTD: totalEarned > 0 ? `$${Math.round(projectedYTD).toLocaleString('en-US')}` : "—",
      longestBooking: longestEntry.days > 0 ? String(longestEntry.days) : "—",
      longestBookingCompany: longestEntry.company,
      monthlyIfStopped: monthlyIfStopped > 0 ? `$${monthlyIfStopped.toLocaleString('en-US')}` : "—",
      totalAverage: monthlyAvg > 0 ? `$${Math.round(monthlyAvg).toLocaleString('en-US')}` : "—",
      isSummaryFallback: false,
      bookedDaysFallback: false,
    };
  }, [yearBookings, selectedYear, billableStats, yearlyPerformance]);

  // Cumulative days worked vs year elapsed (0–100 scale)
  const performanceData = useMemo(() => {
    const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
    const now = new Date();
    const yr = parseInt(selectedYear);
    const isCurrentYear = yr === now.getFullYear();
    const currentMonth = isCurrentYear ? now.getMonth() : 11;
    // Midnight today — used as window ceiling for current month so future days aren't counted as worked
    const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());

    let cumulativeDays = 0;
    return monthNames.map((month, i) => {
      const monthStart = new Date(yr, i, 1);
      const monthEnd = new Date(yr, i + 1, 0); // last calendar day of month i

      // "Year elapsed" pct — capped to today for the current month so the grey
      // baseline never jumps ahead of the worked line mid-month.
      const passedWindowEnd = (isCurrentYear && i === currentMonth) ? todayMidnight : monthEnd;
      const passedPct = billableStats.totalBillableDays > 0
        ? Math.round((countBillableDays(new Date(yr, 0, 1), passedWindowEnd, billableStats.holidays) / billableStats.totalBillableDays) * 100)
        : 0;

      // Future months — no worked data yet
      if (isCurrentYear && i > currentMonth) {
        return { month, worked: null, passed: passedPct };
      }

      // For current month only count through today — committed-but-not-yet-worked days must not
      // inflate "Days Worked". For past months use the full month end.
      const windowEnd = (isCurrentYear && i === currentMonth) ? todayMidnight : monthEnd;

      // Overlap-based counting: accumulate weekdays that fall inside [monthStart, windowEnd]
      // for every booking that overlaps this window. This correctly handles multi-month bookings
      // by attributing only the days that physically land in this month (fixes flat-line bug).
      const monthDays = yearBookings.reduce((s, b) => {
        if (!b.startDate || !b.endDate) return s;
        const bStart = new Date(b.startDate + "T00:00:00");
        const bEnd   = new Date(b.endDate   + "T00:00:00");
        const oStart = bStart > monthStart ? bStart : monthStart;
        const oEnd   = bEnd   < windowEnd  ? bEnd   : windowEnd;
        if (oEnd < oStart) return s;
        const scale = hourlyScale(b);
        return s + countWeekdaysBetween(oStart, oEnd) * scale;
      }, 0);

      cumulativeDays += monthDays;
      const workedPct = billableStats.totalBillableDays > 0
        ? Math.min(100, Math.round((cumulativeDays / billableStats.totalBillableDays) * 100))
        : 0;

      return { month, worked: workedPct, passed: passedPct };
    });
  }, [yearBookings, selectedYear, billableStats]);

  const latestWorkedPct = useMemo(() => {
    const withData = performanceData.filter(d => d.worked !== null);
    return withData.length > 0 ? (withData[withData.length - 1].worked as number) : 0;
  }, [performanceData]);

  const currentPassedPct = useMemo(() => {
    if (billableStats.totalBillableDays === 0) return 0;
    return Math.round((billableStats.billableDaysPassed / billableStats.totalBillableDays) * 100);
  }, [billableStats]);

  const paceStatus = useMemo((): 'ABOVE PACE' | 'ON PACE' | 'NEUTRAL' | 'BEHIND PACE' | null => {
    const yr = parseInt(selectedYear);
    if (yr !== new Date().getFullYear()) return null;
    if (currentPassedPct === 0 || yearBookings.length === 0) return null;
    const ratio = latestWorkedPct / currentPassedPct;
    if (ratio > 1.0)   return 'ABOVE PACE';
    if (ratio >= 0.80) return 'ON PACE';
    if (ratio >= 0.55) return 'NEUTRAL';
    return 'BEHIND PACE';
  }, [latestWorkedPct, currentPassedPct, selectedYear, yearBookings]);

  // Goal-based pacing: days ahead/behind based on annual goal and current avg day rate.
  const goalPaceDays = useMemo(() => {
    if (annualGoal <= 0 || billableStats.totalBillableDays === 0) return null;
    const yr = parseInt(selectedYear);
    if (yr !== new Date().getFullYear()) return null;
    const now = new Date();
    const startedBookings = yearBookings.filter(b => b.startDate && new Date(b.startDate + 'T00:00:00') <= now);
    const workedDays = startedBookings.reduce((s, b) => s + effectiveDays(b), 0);
    const startedEarnings = startedBookings.reduce((s, b) => s + parseEarnings(b.earnings), 0);
    if (workedDays === 0 || startedEarnings === 0) return null;
    const avgRate = startedEarnings / workedDays;
    const goalDays = annualGoal / avgRate;
    const elapsedPct = billableStats.billableDaysPassed / billableStats.totalBillableDays;
    const expectedByNow = goalDays * elapsedPct;
    const diff = Math.round(workedDays - expectedByNow);
    return { diff, goalDays: Math.round(goalDays) };
  }, [annualGoal, yearBookings, selectedYear, billableStats]);

  // Against Last Year: YTD earnings vs same calendar point last year.
  // Returns { thisYear, lastYear, diff } so the UI can display both raw amounts.
  // vsLastYearWorked (booking-pace %) is preserved below in case it's needed later.
  const vsLastYear = useMemo(() => {
    const yr = parseInt(selectedYear);
    const now = new Date();
    const isCurrentYear = yr === now.getFullYear();
    const cutoff = isCurrentYear ? now : new Date(yr, 11, 31);
    const prevMonthMaxDay = new Date(yr - 1, isCurrentYear ? now.getMonth() + 1 : 12, 0).getDate();
    const prevCutoff = isCurrentYear
      ? new Date(yr - 1, now.getMonth(), Math.min(now.getDate(), prevMonthMaxDay))
      : new Date(yr - 1, 11, 31);
    const thisYear = bookings
      .filter(b => { if (!b.startDate) return false; const d = new Date(b.startDate + "T00:00:00"); return d.getFullYear() === yr && d <= cutoff; })
      .reduce((s, b) => s + parseEarnings(b.earnings), 0);
    const lastYear = bookings
      .filter(b => { if (!b.startDate) return false; const d = new Date(b.startDate + "T00:00:00"); return d.getFullYear() === yr - 1 && d <= prevCutoff; })
      .reduce((s, b) => s + parseEarnings(b.earnings), 0);
    if (lastYear === 0) return null; // no previous-year data to compare
    return { thisYear, lastYear, diff: thisYear - lastYear };
  }, [bookings, selectedYear]);

  // Against Annual Goal: started YTD earnings vs pro-rated goal (current year only).
  // Returns { diff, proratedGoal, actual } so the UI can show both the target and the delta.
  const vsAnnualGoal = useMemo(() => {
    const yr = parseInt(selectedYear);
    const now = new Date();
    if (yr !== now.getFullYear() || annualGoal <= 0 || billableStats.totalBillableDays === 0) return null;
    const proratedGoal = Math.round(annualGoal * (billableStats.billableDaysPassed / billableStats.totalBillableDays));
    const allContracted = yearBookings.reduce((s, b) => s + parseEarnings(b.earnings), 0);
    return { diff: allContracted - proratedGoal, proratedGoal, actual: allContracted };
  }, [annualGoal, yearBookings, selectedYear, billableStats]);

  // Goal Pacing — current-year forward-looking metrics against annualGoal.
  // Returns null when annualGoal === 0 (section hidden) OR when a past year is selected.
  // Falls back to yearly_performance summary values when no individual bookings exist.
  const goalPacingStats = useMemo(() => {
    if (annualGoal <= 0) return null;
    const yr = parseInt(selectedYear);
    const now = new Date();
    if (yr !== now.getFullYear()) return null;

    const daysInCurrentMonth = new Date(now.getFullYear(), now.getMonth() + 1, 0).getDate();
    const remainingMonths = Math.min(12, 12 - now.getMonth() - (now.getDate() / daysInCurrentMonth));

    // Use yearly_performance summary when no individual bookings exist for the current year
    const perfRow = yearBookings.length === 0 ? yearlyPerformance[selectedYear] : undefined;

    const ytdEarned = perfRow
      ? (parseEarnings(perfRow.earned) || 0)
      : yearBookings.reduce((s, b) => s + parseEarnings(b.earnings), 0);

    const remaining = Math.max(0, annualGoal - ytdEarned);
    const minMonthly = remainingMonths > 0 ? Math.round(remaining / remainingMonths) : 0;

    // avgRate: from yearly_performance when no bookings, else derived from booking data
    let avgRate = 0;
    if (perfRow) {
      avgRate = perfRow.dayRate !== '—' ? parseEarnings(perfRow.dayRate) : 0;
    } else {
      const totalDays = yearBookings.reduce((s, b) => s + effectiveDays(b), 0);
      const totalEarnings = yearBookings.reduce((s, b) => s + parseEarnings(b.earnings), 0);
      avgRate = totalDays > 0 ? totalEarnings / totalDays : 0;
    }

    const minDaysPerMonth = avgRate > 0 ? Math.ceil(minMonthly / avgRate) : 0;
    const daysToGoal = avgRate > 0 ? Math.ceil(remaining / avgRate) : 0;
    const achievable = daysToGoal <= billableStats.billableDaysRemaining;

    return { remainingMonths, ytdEarned, remaining, minMonthly, avgRate, minDaysPerMonth, daysToGoal, achievable };
  }, [annualGoal, yearBookings, selectedYear, billableStats, yearlyPerformance]);

  // Against Last Year: last year's worked-% at the same calendar point, for the pace pill.
  const vsLastYearWorked = useMemo(() => {
    const yr = parseInt(selectedYear);
    const now = new Date();
    const isCurrentYear = yr === now.getFullYear();
    const lastYr = yr - 1;
    const lastYrBookings = bookings.filter(b =>
      b.startDate && new Date(b.startDate + "T00:00:00").getFullYear() === lastYr);
    if (lastYrBookings.length === 0) return null;
    const lastYrHolidays  = getHolidays(lastYr, holidayCalendar);
    const lastYrTotal     = countBillableDays(new Date(lastYr, 0, 1), new Date(lastYr, 11, 31), lastYrHolidays);
    if (lastYrTotal === 0) return null;
    // Cutoff = same calendar date in the prior year (clamped to valid day)
    const maxPrevDay = new Date(lastYr, isCurrentYear ? now.getMonth() + 1 : 12, 0).getDate();
    const cutoff = isCurrentYear
      ? new Date(lastYr, now.getMonth(), Math.min(now.getDate(), maxPrevDay))
      : new Date(lastYr, 11, 31);
    const lastYrWorked = lastYrBookings
      .filter(b => new Date(b.startDate + "T00:00:00") <= cutoff)
      .reduce((s, b) => s + effectiveDays(b), 0);
    return Math.round((lastYrWorked / lastYrTotal) * 100);
  }, [bookings, selectedYear]);

  // Build effectiveYearlyPerformance from live bookings for ALL years, falling back to
  // yearlyPerformance DB rows only when no bookings exist for that year.
  // The yearly_performance table is only written at onboarding so historical years never
  // appear there unless we derive them from actual booking records.
  const effectiveYearlyPerformance = useMemo(() => {
    const now = new Date();
    if (bookings.length === 0) return yearlyPerformance;

    // Group bookings by year — include ALL contracted bookings (future start dates included)
    const byYear: Record<string, typeof bookings> = {};
    bookings.forEach(b => {
      if (!b.startDate) return;
      const y = String(new Date(b.startDate + "T00:00:00").getFullYear());
      if (!byYear[y]) byYear[y] = [];
      byYear[y].push(b);
    });

    const liveEntries: Record<string, YearStat> = {};
    Object.entries(byYear).forEach(([y, yBookings]) => {
      const totalEarned = yBookings.reduce((s, b) => s + parseEarnings(b.earnings), 0);
      const totalDays   = yBookings.reduce((s, b) => s + effectiveDays(b), 0);
      const avgRate     = totalDays > 0 ? totalEarned / totalDays : 0;
      liveEntries[y] = {
        earned:     `${sym}${Math.round(totalEarned).toLocaleString('en-US')}`,
        billedDays: String(Math.round(totalDays)),
        dayRate:    avgRate > 0 ? `${sym}${Math.round(avgRate).toLocaleString('en-US')}` : '—',
        total:      `${sym}${totalEarned > 0 ? Math.round(totalEarned / 12).toLocaleString('en-US') : 0}`,
      };
    });

    // Live bookings take priority; yearlyPerformance fills in years with no bookings
    return { ...yearlyPerformance, ...liveEntries };
  }, [bookings, yearlyPerformance]);

  // Average booking length by client (top 5, filtered by avgBookingYear)
  const avgBookingData = useMemo(() => {
    const filtered = avgBookingYear === "OVERALL"
      ? bookings
      : bookings.filter(b => b.startDate && String(new Date(b.startDate + "T00:00:00").getFullYear()) === avgBookingYear);
    const companyMap: Record<string, { total: number; count: number; display: string }> = {};
    filtered.forEach(b => {
      if (!b.company) return;
      const key = b.company.trim().toLowerCase();
      if (!companyMap[key]) companyMap[key] = { total: 0, count: 0, display: b.company.trim() };
      companyMap[key].total += b.days;
      companyMap[key].count += 1;
    });
    return Object.entries(companyMap)
      .map(([, data], i) => ({
        client: data.display,
        days: Math.round(data.total / data.count),
        color: CLIENT_COLORS[i % CLIENT_COLORS.length],
      }))
      .sort((a, b) => b.days - a.days)
      .slice(0, 5);
  }, [bookings, avgBookingYear]);

  const maxAvgDays = avgBookingData.length > 0 ? Math.max(...avgBookingData.map(d => d.days)) : 1;

  // Most valuable clients (top 3 by total days, filtered by clientsYear)
  const topClients = useMemo(() => {
    const filtered = clientsYear === "OVERALL"
      ? bookings
      : bookings.filter(b => b.startDate && String(new Date(b.startDate + "T00:00:00").getFullYear()) === clientsYear);
    const companyMap: Record<string, { days: number; display: string }> = {};
    filtered.forEach(b => {
      if (!b.company) return;
      const key = b.company.trim().toLowerCase();
      if (!companyMap[key]) companyMap[key] = { days: 0, display: b.company.trim() };
      companyMap[key].days += effectiveDays(b);
    });
    const sorted = Object.entries(companyMap).sort(([, a], [, b]) => b.days - a.days);
    const totalDays = sorted.reduce((s, [, d]) => s + d.days, 0);
    return sorted.slice(0, 3).map(([, { days, display }]) => ({
      name: display,
      pct: totalDays > 0 ? `${Math.round((days / totalDays) * 100)}%` : "0%",
    }));
  }, [bookings, clientsYear]);

  // RevenueChart bars from effectiveYearlyPerformance (live current-year data merged in)
  const revenueChartBars = useMemo(() => {
    const entries = Object.entries(effectiveYearlyPerformance);
    if (entries.length === 0) return [];
    const maxEarned = Math.max(...entries.map(([, s]) => parseEarnings(s.earned)));
    if (maxEarned === 0) return [];
    return entries
      .sort(([a], [b]) => parseInt(a) - parseInt(b))
      .map(([year, stat]) => ({
        year,
        value: Math.max(5, Math.round((parseEarnings(stat.earned) / maxEarned) * 90)),
      }));
  }, [effectiveYearlyPerformance]);

  // Seasonal activity — billed days per month, historical avg vs selected year
  const seasonalData = useMemo(() => {
    const now = new Date();
    const selectedYr = parseInt(selectedYear);
    const isCurrentYear = selectedYr === now.getFullYear();
    const monthNames = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];

    const currentYearNum = now.getFullYear();
    const historicalBookings = bookings.filter(b => {
      if (!b.startDate) return false;
      const yr = new Date(b.startDate + "T00:00:00").getFullYear();
      if (yr === selectedYr) return false;
      // exclude the current in-progress year from the average when viewing a past year
      if (selectedYr !== currentYearNum && yr === currentYearNum) return false;
      return true;
    });
    const selectedYrBookings = bookings.filter(b =>
      b.startDate && new Date(b.startDate + "T00:00:00").getFullYear() === selectedYr
    );

    const historicalYears = Array.from(new Set(
      historicalBookings.map(b => new Date(b.startDate + "T00:00:00").getFullYear())
    )).sort((a, b) => a - b);

    const avgDays = Array.from({ length: 12 }, (_, mi) => {
      if (historicalYears.length === 0) return 0;
      const total = historicalYears.reduce((sum, yr) => {
        const yrBooks = historicalBookings.filter(b =>
          new Date(b.startDate + "T00:00:00").getFullYear() === yr
        );
        const monthStart = new Date(yr, mi, 1);
        const monthEnd   = new Date(yr, mi + 1, 0);
        return sum + yrBooks.reduce((t, b) => {
          if (!b.startDate || !b.endDate) return t;
          const bStart = new Date(b.startDate + "T00:00:00");
          const bEnd   = new Date(b.endDate   + "T00:00:00");
          const oStart = bStart > monthStart ? bStart : monthStart;
          const oEnd   = bEnd   < monthEnd   ? bEnd   : monthEnd;
          if (oEnd < oStart) return t;
          return t + countWeekdaysBetween(oStart, oEnd) * hourlyScale(b);
        }, 0);
      }, 0);
      return Math.round((total / historicalYears.length) * 10) / 10;
    });

    const currentMonthIdx = isCurrentYear ? now.getMonth() : 11;
    const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const thisYearDays: (number | undefined)[] = Array.from({ length: 12 }, (_, mi) => {
      if (isCurrentYear && mi > currentMonthIdx) return undefined;
      const monthStart = new Date(selectedYr, mi, 1);
      const windowEnd  = (isCurrentYear && mi === currentMonthIdx)
        ? todayMidnight
        : new Date(selectedYr, mi + 1, 0);
      const days = selectedYrBookings.reduce((t, b) => {
        if (!b.startDate || !b.endDate) return t;
        const bStart = new Date(b.startDate + "T00:00:00");
        const bEnd   = new Date(b.endDate   + "T00:00:00");
        const oStart = bStart > monthStart ? bStart : monthStart;
        const oEnd   = bEnd   < windowEnd  ? bEnd   : windowEnd;
        if (oEnd < oStart) return t;
        return t + countWeekdaysBetween(oStart, oEnd) * hourlyScale(b);
      }, 0);
      return Math.round(days * 10) / 10;
    });

    const busiestHistIdx = avgDays.length > 0 ? avgDays.indexOf(Math.max(...avgDays)) : -1;
    const tyWithData = thisYearDays.filter(d => d !== undefined) as number[];
    const busiestTYIdx = tyWithData.length > 0
      ? thisYearDays.indexOf(Math.max(...tyWithData))
      : -1;
    const monthsAboveAvg = thisYearDays.filter((d, i) => d !== undefined && d > avgDays[i]).length;

    const minHist = historicalYears.length > 0 ? historicalYears[0] : null;
    const maxHist = historicalYears.length > 0 ? historicalYears[historicalYears.length - 1] : null;
    const yearRange = minHist === null ? null
      : minHist === maxHist ? String(minHist)
      : `${minHist}–${maxHist}`;

    const chartData = monthNames.map((month, i) => ({
      month,
      avg: historicalYears.length > 0 ? avgDays[i] : undefined,
      thisYear: thisYearDays[i],
    }));

    return {
      chartData,
      historicalYears,
      yearRange,
      busiestHistMonth: busiestHistIdx >= 0 ? monthNames[busiestHistIdx] : null,
      busiestHistDays: busiestHistIdx >= 0 ? Math.round(avgDays[busiestHistIdx]) : 0,
      busiestThisYear: busiestTYIdx >= 0 ? monthNames[busiestTYIdx] : null,
      busiestThisYearDays: busiestTYIdx >= 0 ? Math.round(tyWithData[busiestTYIdx]) : null,
      monthsAboveAvg,
      monthsWithData: tyWithData.length,
      avgYearlyDays: Math.round(avgDays.reduce((sum, d) => sum + d, 0)),
      hasHistoricalData: historicalYears.length > 0,
    };
  }, [bookings, holidayCalendar, selectedYear]);

  function ss(val: string) { return val.replace(/\$/g, sym); }

  // YTD earnings — computed from bookings (endDate <= today, current year)
  const ytdEarningsRaw = useMemo(() => {
    const now = new Date();
    const currentYear = now.getFullYear();
    return bookings
      .filter(b => b.startDate &&
        new Date(b.startDate + "T00:00:00").getFullYear() === currentYear &&
        new Date(b.startDate + "T00:00:00") <= now)
      .reduce((sum, b) => sum + parseEarnings(b.earnings), 0);
  }, [bookings]);
  const ytdDisplay     = ss("$" + ytdEarningsRaw.toLocaleString('en-US'));
  const reserveDisplay = ss("$" + Math.round(ytdEarningsRaw * taxReservePct / 100).toLocaleString('en-US'));

  const stats = computedStats;

  const pageClasses          = isDark ? "bg-gradient-to-b from-[#0f1419] via-[#131820] to-[#0a0e12] text-white" : "bg-[#E3E8F1] text-[#1A1F36]";
  const labelClasses         = isDark ? "text-white/40" : "text-slate-600";
  const textClasses          = isDark ? "text-white" : "text-slate-900";
  const secondaryTextClasses = isDark ? "text-white/50" : "text-slate-500";
  const borderClasses        = isDark ? "border-white/10" : "border-black/5";
  const cardBgClasses        = isDark ? "bg-gradient-to-br from-white/5 to-white/[0.02]" : "bg-white/90 backdrop-blur-2xl shadow-sm [@supports_not_(backdrop-filter:blur(1px))]:bg-white";
  const prefersReducedMotion = typeof window !== 'undefined' && window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  // Bubble chart layout (positions stay fixed; labels + pcts are dynamic)
  const bubbleLayout = [
    { x: 67,  y: 58,  w: 198, h: 96, r: 48, color: isDark ? "rgba(148,163,184,0.35)" : "rgba(148,163,184,0.4)",  tx: 124, ty: 100, ta: "middle" as const, ptx: 124, pta: "middle" as const },
    { x: 179, y: 75,  w: 166, h: 84, r: 42, color: isDark ? "rgba(147,197,253,0.35)" : "rgba(147,197,253,0.5)",  tx: 337, ty: 104, ta: "end"    as const, ptx: 316, pta: "end"    as const },
    { x: 130, y: 110, w: 140, h: 72, r: 36, color: isDark ? "rgba(196,181,253,0.35)" : "rgba(196,181,253,0.5)",  tx: 198, ty: 146, ta: "middle" as const, ptx: 198, pta: "middle" as const },
  ];

  return (
    <div className={`min-h-screen ${pageClasses} relative overflow-hidden transition-colors duration-300`}>


      {/* Lead gate overlay — shown to Junior active subscribers */}
      {showLeadGate && (
        <div className="fixed inset-0 z-[100] backdrop-blur-md bg-white/70 dark:bg-black/60 flex items-center justify-center overflow-y-auto [@supports_not_(backdrop-filter:blur(1px))]:bg-white/95 dark:[@supports_not_(backdrop-filter:blur(1px))]:bg-black/85">
          <div className="bg-white dark:bg-white/10 rounded-[2rem] p-8 shadow-xl max-w-sm w-full text-center border border-white [@supports_not_(backdrop-filter:blur(1px))]:bg-white">
            <Lock className="w-8 h-8 text-[#4A6CF7] mx-auto mb-4" />
            <h3 className="text-2xl font-black text-[#1A1F36]">Lead Plan Required</h3>
            <p className="text-[11px] font-bold uppercase tracking-[0.2em] text-[#1A1F36]/40 mt-2 mb-6">
              Advanced analytics are available on the Lead plan.
            </p>
            <ul className="space-y-2 mb-6 text-left">
              {['Everything in Junior', 'Estimated Taxes & Payouts', 'Advanced Analytics', 'Invoice Management'].map(f => (
                <li key={f} className="flex items-center gap-2 text-xs font-bold text-[#1A1F36]/70">
                  <svg width="12" height="12" viewBox="0 0 12 12" fill="none"><path d="M2 6l3 3 5-5" stroke="#4A6CF7" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/></svg>
                  {f}
                </li>
              ))}
            </ul>
            <button
              onClick={() => navigate('/settings#subscription-section')}
              className="bg-black text-white rounded-full font-black uppercase tracking-[0.2em] px-8 py-3 text-[10px] hover:bg-gray-900 transition-colors w-full"
            >
              Upgrade to Lead — $9.50/mo
            </button>
          </div>
        </div>
      )}

      <div className="fixed inset-0 pointer-events-none overflow-hidden">
        <div className={`absolute -top-40 -right-40 w-96 h-96 rounded-full blur-3xl ${prefersReducedMotion ? '' : 'animate-pulse'} ${isDark ? "bg-blue-600/10" : "bg-blue-300/10"}`} />
        <div className={`absolute -bottom-40 -left-40 w-96 h-96 rounded-full blur-3xl ${prefersReducedMotion ? '' : 'animate-pulse'} ${isDark ? "bg-purple-600/10" : "bg-purple-300/10"}`} style={{ animationDelay: "1s" }} />
      </div>

      <main className="max-w-[1400px] mx-auto relative z-10 space-y-8 p-4 md:p-8 pt-4 pb-28 md:pb-8">

        {/* Header */}
        <motion.header initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: prefersReducedMotion ? 0 : 0.5 }}
          className="flex justify-between items-start gap-4 md:gap-8">
          <div className="flex-1">
            <h1 className={`text-3xl sm:text-5xl md:text-7xl lg:text-8xl font-black ${textClasses} tracking-tight mb-2`}>Stats</h1>
            <p className={`ml-3 ${secondaryTextClasses} font-light text-base sm:text-lg md:text-xl`}>Let's get a better picture of the biz.</p>
          </div>
          <div className="flex items-center gap-4">
            <motion.button whileHover={{ scale: 1.05 }} whileTap={{ scale: 0.95 }} onClick={toggleTheme}
              className={`w-10 h-10 rounded-lg flex items-center justify-center transition-all ${isDark ? "bg-white/10 text-white/70 hover:bg-white/20" : "bg-white/60 text-[#1A1F36] hover:bg-white border border-white/60 shadow-sm"}`}>
              {isDark ? <SunIcon /> : <MoonIcon />}
            </motion.button>
          </div>
        </motion.header>

        {/* Year Selector */}
        <div className="flex justify-end">
          <div className="relative">
            <button onClick={() => setYearDropdownOpen(v => !v)}
              className={`text-xs font-black uppercase tracking-widest transition-all ${isDark ? "text-white/30 hover:text-white/60" : "text-[#1A1F36]/30 hover:text-[#1A1F36]/60"}`}>
              {selectedYear} ▾
            </button>
            {yearDropdownOpen && (
              <div className={`absolute right-0 top-7 z-50 rounded-2xl border shadow-xl overflow-hidden ${isDark ? "bg-[#1a1f2e] border-white/10" : "bg-white border-gray-200"}`}>
                {statYears.map(year => (
                  <button key={year} onClick={() => { setSelectedYear(year); setYearDropdownOpen(false); }}
                    className={`block w-full text-left px-6 py-3 text-xs font-black uppercase tracking-widest transition-all ${
                      selectedYear === year ? "text-[#4A6CF7]"
                      : isDark ? "text-white/40 hover:text-white hover:bg-white/5"
                      : "text-[#1A1F36]/40 hover:text-[#1A1F36] hover:bg-gray-50"
                    }`}>{year}</button>
                ))}
              </div>
            )}
          </div>
        </div>

        {/* Row 1 — Availability & Activity */}
        <div className={`border-t ${borderClasses} pt-12`}>
          <p className={`${labelClasses} text-[10px] font-bold uppercase tracking-[0.2em] mb-3`}>Availability &amp; Activity</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { label: "Hired Percentage",                  value: stats.hiredPct,              sub: null as string | null, helpSlug: "hired-percentage" as string | null },
              { label: "Booked Days This Year",             value: stats.bookedDays,            sub: stats.bookedDaysFallback ? "From annual summary" : null, helpSlug: null },
              { label: "Billable Days Remaining This Year", value: stats.billableDaysRemaining, sub: null as string | null, helpSlug: null },
              { label: "Billable Year Remaining", value: stats.billableYearRemaining, sub: null as string | null, helpSlug: null },
            ].map((card, i) => (
              <motion.div key={card.label}
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.1 + i * 0.05 }} whileHover={{ y: -4 }}
                className={`${cardBgClasses} border ${borderClasses} rounded-[1.5rem] p-6 h-[160px] flex flex-col transition-all`}>
                <div className="flex items-center gap-2 mb-3">
                  <p className={`${labelClasses} text-[14px] font-bold uppercase tracking-[0.2em]`}>{card.label}</p>
                  {card.helpSlug && (
                    <Link to={`/help?article=${card.helpSlug}`} title="Hired Percentage and Days Booked, Explained" className={`w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center text-[9px] font-black transition-all ${isDark ? "bg-white/10 text-white/30 hover:bg-white hover:text-black" : "bg-black/5 text-[#1A1F36]/25 hover:bg-black hover:text-white"}`}>?</Link>
                  )}
                </div>
                <div className="flex-1 flex flex-col justify-end">
                  {bookingsLoading ? (
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full border-2 border-[#4A6CF7]/30 border-t-[#4A6CF7] animate-spin" />
                      <span className={`text-[11px] font-bold uppercase tracking-[0.2em] opacity-30 ${isDark ? "text-white" : "text-[#1A1F36]"}`}>Loading</span>
                    </div>
                  ) : (
                    <>
                      {card.value.includes(' of ') ? (() => {
                        const [main, denom] = card.value.split(' of ');
                        return (
                          <p className={`text-4xl md:text-5xl font-black leading-none ${textClasses}`}>
                            {main}<span className={`text-xl font-bold opacity-40 ml-1`}> of {denom}</span>
                          </p>
                        );
                      })() : (
                        <p className={`text-4xl md:text-5xl font-black ${textClasses}`}>{card.value}</p>
                      )}
                      {card.value === "—" && <p className={`text-[10px] font-bold uppercase tracking-[0.2em] mt-1 opacity-40 ${isDark ? "text-white" : "text-[#1A1F36]"}`}>Add bookings to see this stat</p>}
                      {card.value !== "—" && card.sub && <p className={`${secondaryTextClasses} text-xs font-light mt-1`}>{card.sub}</p>}
                    </>
                  )}
                </div>
              </motion.div>
            ))}
          </div>

          {/* Historical Monthly Trends — full width below stat cards */}
          {seasonalData.hasHistoricalData && (
            <motion.div
              initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.5, delay: 0.45 }}
              className={`${cardBgClasses} border ${borderClasses} rounded-[2.5rem] p-6 md:p-12 mt-6`}
            >
              <div className="flex items-center gap-2 mb-1">
                <p className={`${labelClasses} text-[14px] font-bold uppercase tracking-[0.2em]`}>Historical Monthly Trends</p>
                <Link to="/help?article=seasonal-activity" title="Historical Monthly Trends, Explained" className={`w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center text-[9px] font-black transition-all ${isDark ? "bg-white/10 text-white/30 hover:bg-white hover:text-black" : "bg-black/5 text-[#1A1F36]/25 hover:bg-black hover:text-white"}`}>?</Link>
              </div>
              {seasonalData.yearRange && (
                <p className={`${secondaryTextClasses} text-xs font-light mb-5`}>Average across {seasonalData.yearRange}</p>
              )}

              {/* Compact stat sub-cards */}
              {seasonalData.busiestHistMonth && (() => {
                const FULL = ["January","February","March","April","May","June","July","August","September","October","November","December"];
                const abbrs = ["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"];
                const histFull = FULL[abbrs.indexOf(seasonalData.busiestHistMonth)] ?? seasonalData.busiestHistMonth;
                const tyFull   = seasonalData.busiestThisYear ? (FULL[abbrs.indexOf(seasonalData.busiestThisYear)] ?? seasonalData.busiestThisYear) : null;
                return (
                  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
                    <div className={`${isDark ? "bg-white/5" : "bg-black/[0.03]"} rounded-2xl p-4 flex flex-col`}>
                      <p className={`${labelClasses} text-[10px] font-bold uppercase tracking-[0.2em] mb-2`}>Historically Busiest</p>
                      <p className={`text-2xl font-black ${textClasses} mb-0.5`}>{histFull}</p>
                      <p className={`${secondaryTextClasses} text-xs font-light`}>avg {seasonalData.busiestHistDays} days</p>
                    </div>
                    {tyFull && (
                      <div className={`${isDark ? "bg-white/5" : "bg-black/[0.03]"} rounded-2xl p-4 flex flex-col`}>
                        <p className={`${labelClasses} text-[10px] font-bold uppercase tracking-[0.2em] mb-2`}>Busiest in {selectedYear}</p>
                        <p className={`text-2xl font-black ${isDark ? "text-blue-400" : "text-blue-600"} mb-0.5`}>{tyFull}</p>
                        <p className={`${secondaryTextClasses} text-xs font-light`}>{seasonalData.busiestThisYearDays} days booked</p>
                      </div>
                    )}
                    {seasonalData.avgYearlyDays > 0 && (
                      <div className={`${isDark ? "bg-white/5" : "bg-black/[0.03]"} rounded-2xl p-4 flex flex-col`}>
                        <p className={`${labelClasses} text-[10px] font-bold uppercase tracking-[0.2em] mb-2`}>Avg Yearly Days</p>
                        <p className={`text-2xl font-black ${textClasses} mb-0.5`}>{seasonalData.avgYearlyDays}</p>
                        <p className={`${secondaryTextClasses} text-xs font-light`}>days across {seasonalData.yearRange}</p>
                      </div>
                    )}
                  </div>
                );
              })()}

              {bookingsLoading ? (
                <div className="flex items-center justify-center h-52 mt-4">
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full border-2 border-[#4A6CF7]/30 border-t-[#4A6CF7] animate-spin" />
                    <span className={`text-[11px] font-bold uppercase tracking-[0.2em] opacity-30 ${isDark ? "text-white" : "text-[#1A1F36]"}`}>Loading</span>
                  </div>
                </div>
              ) : (
                <div className="mt-4">
                  {(() => {
                    const lastTYIdx = seasonalData.chartData.reduce(
                      (acc, d, i) => d.thisYear !== undefined ? i : acc, -1
                    );
                    const lineColor  = isDark ? "#60a5fa" : "#2563eb";
                    const dotFill    = "#4A6CF7";
                    const dotStroke  = isDark ? "#1a1f2e" : "#ffffff";
                    const barColor   = isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.16)";
                    const gridColor2 = isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)";
                    const tickColor2 = isDark ? "rgba(255,255,255,0.3)" : "#94a3b8";
                    const allVals    = seasonalData.chartData.flatMap(d => [d.avg ?? 0, d.thisYear ?? 0]);
                    const yMax       = Math.max(30, ...allVals);

                    return (
                      <ResponsiveContainer width="100%" height={220}>
                        <ComposedChart data={seasonalData.chartData} margin={{ top: 8, right: 8, left: 5, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={gridColor2} />
                          <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: tickColor2, fontWeight: 600 }} dy={8} interval={0} padding={{ left: 15, right: 5 }} />
                          <YAxis
                            domain={[0, yMax]}
                            ticks={Array.from(new Set([0, 8, 16, 21, yMax])).sort((a, b) => a - b)}
                            axisLine={false} tickLine={false}
                            tick={({ x, y, payload }: any) => (
                              <text x={x} y={y} dy={3} textAnchor="end" fontSize={9} fontWeight={payload.value === 21 ? 700 : 600} fill={payload.value === 21 ? (isDark ? "rgba(255,255,255,0.5)" : "#4b5563") : tickColor2}>
                                {payload.value}
                              </text>
                            )}
                            width={28} allowDecimals={false}
                          />
                          <Tooltip
                            content={({ active, payload, label }: any) => {
                              if (!active || !payload?.length) return null;
                              const avg = payload.find((p: any) => p.dataKey === 'avg')?.value;
                              const ty  = payload.find((p: any) => p.dataKey === 'thisYear')?.value;
                              if (avg === undefined && ty === undefined) return null;
                              const diff = ty !== undefined && avg !== undefined ? Math.round((ty - avg) * 10) / 10 : null;
                              const sign = diff !== null && diff >= 0 ? '+' : '';
                              return (
                                <div className={`backdrop-blur-md border px-4 py-3 rounded-xl shadow-lg min-w-[180px] ${isDark ? "bg-white/10 border-white/10 text-white" : "bg-white/95 border-slate-100 text-slate-900"}`}>
                                  <span className={`text-[10px] font-bold uppercase tracking-widest ${isDark ? "text-white/40" : "text-slate-400"}`}>{label}</span>
                                  {ty !== undefined && (
                                    <div className="mt-2 mb-1">
                                      <span className="text-base font-black">{ty} days · {selectedYear}</span>
                                    </div>
                                  )}
                                  {avg !== undefined && (
                                    <div className={`text-[11px] mb-2.5 ${isDark ? "text-white/40" : "text-slate-400"}`}>avg {avg} days</div>
                                  )}
                                  {diff !== null && (
                                    <div className={`text-[11px] font-bold pt-2 border-t ${isDark ? "border-white/10" : "border-slate-100"} ${diff >= 0 ? (isDark ? "text-green-400" : "text-green-600") : (isDark ? "text-amber-400" : "text-amber-600")}`}>{sign}{diff} days vs Avg</div>
                                  )}
                                </div>
                              );
                            }}
                            cursor={{ stroke: gridColor2, strokeWidth: 1, strokeDasharray: "4 4" }}
                            isAnimationActive={false}
                          />
                          <Bar dataKey="avg" fill={barColor} radius={[4, 4, 0, 0]} maxBarSize={22} animationDuration={1500} />
                          <Line
                            type="monotone" dataKey="thisYear" stroke={lineColor} strokeWidth={3}
                            dot={(props: any) => {
                              if (props.value === null || props.value === undefined) return <g key={props.index} />;
                              const isLast = props.index === lastTYIdx;
                              return <circle key={props.index} cx={props.cx} cy={props.cy} r={isLast ? 5 : 3} fill={dotFill} stroke={isLast ? dotStroke : "none"} strokeWidth={isLast ? 2 : 0} />;
                            }}
                            activeDot={{ r: 7, strokeWidth: 0, fill: dotFill }}
                            connectNulls={false} animationDuration={2000}
                          />
                          <ReferenceLine y={21} stroke={isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.15)"} strokeWidth={1.5} strokeDasharray="6 4" />
                        </ComposedChart>
                      </ResponsiveContainer>
                    );
                  })()}

                  <div className="flex gap-6 mt-4 flex-wrap">
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-2.5 rounded-[2px]" style={{ background: isDark ? "rgba(255,255,255,0.12)" : "rgba(0,0,0,0.16)" }} />
                      <span className={`text-[10px] font-bold uppercase tracking-widest ${secondaryTextClasses}`}>Historical Avg</span>
                    </div>
                    {seasonalData.monthsWithData > 0 && (
                      <div className="flex items-center gap-2">
                        <div className={`w-5 h-0.5 rounded-full ${isDark ? "bg-blue-400" : "bg-blue-500"}`} />
                        <span className={`text-[10px] font-bold uppercase tracking-widest ${secondaryTextClasses}`}>This Year</span>
                      </div>
                    )}
                    <div className="flex items-center gap-2">
                      <svg width="20" height="4" style={{ display: 'block' }}>
                        <line x1="0" y1="2" x2="20" y2="2" stroke={isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.15)"} strokeWidth="1.5" strokeDasharray="5 3" />
                      </svg>
                      <span className={`text-[10px] font-bold uppercase tracking-widest ${secondaryTextClasses}`}>Bookable Days</span>
                    </div>
                  </div>
                </div>
              )}
            </motion.div>
          )}
        </div>

        {/* Row 2 — Earnings */}
        <div className={`border-t ${borderClasses} pt-12`}>
          <p className={`${labelClasses} text-[10px] font-bold uppercase tracking-[0.2em] mb-3`}>Revenue</p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { label: "Monthly Avg (YTD)",  value: ss(stats.monthlyAvg),              sub: stats.isSummaryFallback ? "Based on annual summary" : "This Year's Monthly Income",               color: isDark ? "text-blue-400" : "text-blue-600" },
              { label: "Projected EOY",      value: ss(stats.projectedYTD),            sub: stats.isSummaryFallback ? "Based on annual summary" : "If Bookings Continue At This Pace",         color: isDark ? "text-blue-400" : "text-blue-600" },
              { label: "Longest Booking",    value: stats.longestBooking,              sub: stats.longestBookingCompany,                color: null },
              { label: "Monthly If Stopped", value: ss(stats.monthlyIfStopped),        sub: stats.isSummaryFallback ? "Based on annual summary" : "Annual Monthly Earning, If You Stop",        color: isDark ? "text-blue-400" : "text-blue-600" },
            ].map((card, i) => (
              <motion.div key={card.label}
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.15 + i * 0.05 }} whileHover={{ y: -4 }}
                className={`${cardBgClasses} border ${borderClasses} rounded-[1.5rem] p-6 h-[160px] flex flex-col transition-all`}>
                <p className={`${labelClasses} text-[14px] font-bold uppercase tracking-[0.2em] mb-3`}>{card.label}</p>
                <div className="flex-1 flex flex-col justify-end">
                  {bookingsLoading ? (
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full border-2 border-[#4A6CF7]/30 border-t-[#4A6CF7] animate-spin" />
                      <span className={`text-[11px] font-bold uppercase tracking-[0.2em] opacity-30 ${isDark ? "text-white" : "text-[#1A1F36]"}`}>Loading</span>
                    </div>
                  ) : (
                    <>
                      <p className={`text-4xl md:text-5xl font-black ${card.color ?? textClasses}`}>{card.value}</p>
                      {card.value === "—" && <p className={`text-[10px] font-bold uppercase tracking-[0.2em] mt-1 opacity-40 ${isDark ? "text-white" : "text-[#1A1F36]"}`}>Add bookings to see this stat</p>}
                      {card.value !== "—" && card.sub && <p className={`${secondaryTextClasses} text-xs font-light mt-1`}>{card.sub}</p>}
                    </>
                  )}
                </div>
              </motion.div>
            ))}
          </div>
          {/* Revenue chart — full width below earnings cards */}
          <div className="mt-6">
            {revenueChartBars.length === 0 ? (
              <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.4 }}
                className={`${cardBgClasses} border ${borderClasses} rounded-[2.5rem] p-6 md:p-12 min-h-[220px] flex flex-col items-center justify-center`}>
                <p className={`text-[11px] font-bold uppercase tracking-[0.3em] text-center ${secondaryTextClasses} opacity-50 leading-relaxed`}>
                  No performance data yet.<br/>Complete your first year to see trends.
                </p>
              </motion.div>
            ) : (
              <RevenueChart
                totalAverage={ss(stats.totalAverage)}
                yearlyData={Object.keys(effectiveYearlyPerformance).length > 0 ? effectiveYearlyPerformance : undefined}
                barHeights={revenueChartBars.length > 0 ? revenueChartBars : undefined}
              />
            )}
          </div>
        </div>

        {/* Row 2.5 — Goal Pacing + Booking Pace */}
        <div className={`border-t ${borderClasses} pt-12`}>
          <p className={`${labelClasses} text-[10px] font-bold uppercase tracking-[0.2em] mb-3`}>{annualGoal > 0 ? `Goal Pacing (${sym}${annualGoal.toLocaleString('en-US')})` : 'Goal Pacing'}</p>
          {annualGoal > 0 ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">

              {/* Card 1 — Remaining Months */}
              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.15 }} whileHover={{ y: -4 }}
                className={`${cardBgClasses} border ${borderClasses} rounded-[1.5rem] p-6 h-[160px] flex flex-col transition-all`}>
                <p className={`${labelClasses} text-[14px] font-bold uppercase tracking-[0.2em] mb-3`}>Remaining Months</p>
                <div className="flex-1 flex flex-col justify-end">
                  {bookingsLoading ? (
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full border-2 border-[#4A6CF7]/30 border-t-[#4A6CF7] animate-spin" />
                      <span className={`text-[11px] font-bold uppercase tracking-[0.2em] opacity-30 ${isDark ? "text-white" : "text-[#1A1F36]"}`}>Loading</span>
                    </div>
                  ) : (
                    <>
                      {goalPacingStats?.remaining <= 0 ? (
                        <div className={`flex items-center justify-center gap-1.5 ${isDark ? "text-green-400" : "text-green-600"}`}>
                          <span className="text-[10px] font-bold uppercase tracking-[0.2em]">Goal Met</span>
                          <CheckCircle2 className="w-6 h-6 flex-shrink-0" />
                        </div>
                      ) : (
                        <>
                          <p className={`text-4xl md:text-5xl font-black ${textClasses}`}>
                            {!goalPacingStats ? "—" : goalPacingStats.remainingMonths.toFixed(1)}
                          </p>
                          <div className="h-8 mt-1">
                            {!goalPacingStats ? (
                              <p className={`text-[10px] font-bold uppercase tracking-[0.2em] opacity-40 ${isDark ? "text-white" : "text-[#1A1F36]"}`}>Current year only</p>
                            ) : (
                              <p className={`${secondaryTextClasses} text-xs font-light`}>Months left in the year</p>
                            )}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              </motion.div>

              {/* Card 2 — Monthly Earnings Needed */}
              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.2 }} whileHover={{ y: -4 }}
                className={`${cardBgClasses} border ${borderClasses} rounded-[1.5rem] p-6 h-[160px] flex flex-col transition-all`}>
                <p className={`${labelClasses} text-[14px] font-bold uppercase tracking-[0.2em] mb-3`}>Monthly Earnings Needed</p>
                <div className="flex-1 flex flex-col justify-end">
                  {bookingsLoading ? (
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full border-2 border-[#4A6CF7]/30 border-t-[#4A6CF7] animate-spin" />
                      <span className={`text-[11px] font-bold uppercase tracking-[0.2em] opacity-30 ${isDark ? "text-white" : "text-[#1A1F36]"}`}>Loading</span>
                    </div>
                  ) : (
                    <>
                      {goalPacingStats?.remaining <= 0 ? (
                        <div className={`flex items-center justify-center gap-1.5 ${isDark ? "text-green-400" : "text-green-600"}`}>
                          <span className="text-[10px] font-bold uppercase tracking-[0.2em]">Goal Met</span>
                          <CheckCircle2 className="w-6 h-6 flex-shrink-0" />
                        </div>
                      ) : (
                        <>
                          <p className={`text-4xl md:text-5xl font-black ${!goalPacingStats ? textClasses : (isDark ? "text-blue-400" : "text-blue-600")}`}>
                            {!goalPacingStats ? "—" : `${sym}${goalPacingStats.minMonthly.toLocaleString('en-US')}`}
                          </p>
                          {/* fixed-height tail keeps value aligned with other cards */}
                          <div className="h-8 mt-1">
                            {!goalPacingStats && (
                              <p className={`text-[10px] font-bold uppercase tracking-[0.2em] opacity-40 ${isDark ? "text-white" : "text-[#1A1F36]"}`}>Current year only</p>
                            )}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              </motion.div>

              {/* Card 3 — Min. Days Per Month */}
              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.25 }} whileHover={{ y: -4 }}
                className={`${cardBgClasses} border ${borderClasses} rounded-[1.5rem] p-6 h-[160px] flex flex-col transition-all`}>
                <p className={`${labelClasses} text-[14px] font-bold uppercase tracking-[0.2em] mb-3`}>Min. Days Per Month</p>
                <div className="flex-1 flex flex-col justify-end">
                  {bookingsLoading ? (
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full border-2 border-[#4A6CF7]/30 border-t-[#4A6CF7] animate-spin" />
                      <span className={`text-[11px] font-bold uppercase tracking-[0.2em] opacity-30 ${isDark ? "text-white" : "text-[#1A1F36]"}`}>Loading</span>
                    </div>
                  ) : (
                    <>
                      {goalPacingStats?.remaining <= 0 ? (
                        <div className={`flex items-center justify-center gap-1.5 ${isDark ? "text-green-400" : "text-green-600"}`}>
                          <span className="text-[10px] font-bold uppercase tracking-[0.2em]">Goal Met</span>
                          <CheckCircle2 className="w-6 h-6 flex-shrink-0" />
                        </div>
                      ) : (
                        <>
                          <p className={`text-4xl md:text-5xl font-black ${textClasses}`}>
                            {!goalPacingStats ? "—" : goalPacingStats.avgRate === 0 ? "—" : String(goalPacingStats.minDaysPerMonth)}
                          </p>
                          <div className="min-h-[2rem] mt-1">
                            {!goalPacingStats ? (
                              <p className={`text-[10px] font-bold uppercase tracking-[0.2em] opacity-40 ${isDark ? "text-white" : "text-[#1A1F36]"}`}>Current year only</p>
                            ) : goalPacingStats.avgRate === 0 ? (
                              <p className={`text-[10px] font-bold uppercase tracking-[0.2em] opacity-40 ${isDark ? "text-white" : "text-[#1A1F36]"}`}>Add bookings to see this stat</p>
                            ) : (
                              <p className={`${secondaryTextClasses} text-xs font-light`}>Min days booked monthly for goal</p>
                            )}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              </motion.div>

              {/* Card 4 — Days to Goal */}
              <motion.div
                initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: 0.3 }} whileHover={{ y: -4 }}
                className={`${cardBgClasses} border ${borderClasses} rounded-[1.5rem] p-6 h-[160px] flex flex-col transition-all`}>
                <p className={`${labelClasses} text-[14px] font-bold uppercase tracking-[0.2em] mb-3`}>Days to Goal</p>
                <div className="flex-1 flex flex-col justify-end">
                  {bookingsLoading ? (
                    <div className="flex items-center gap-3">
                      <div className="w-6 h-6 rounded-full border-2 border-[#4A6CF7]/30 border-t-[#4A6CF7] animate-spin" />
                      <span className={`text-[11px] font-bold uppercase tracking-[0.2em] opacity-30 ${isDark ? "text-white" : "text-[#1A1F36]"}`}>Loading</span>
                    </div>
                  ) : (
                    <>
                      {goalPacingStats?.remaining <= 0 ? (
                        <div className={`flex items-center justify-center gap-1.5 ${isDark ? "text-green-400" : "text-green-600"}`}>
                          <span className="text-[10px] font-bold uppercase tracking-[0.2em]">Goal Met</span>
                          <CheckCircle2 className="w-6 h-6 flex-shrink-0" />
                        </div>
                      ) : (
                        <>
                          <p className={`text-4xl md:text-5xl font-black ${textClasses}`}>
                            {!goalPacingStats ? "—" : goalPacingStats.avgRate === 0 ? "—" : String(goalPacingStats.daysToGoal)}
                          </p>
                          <div className="h-8 mt-1">
                            {!goalPacingStats ? (
                              <p className={`text-[10px] font-bold uppercase tracking-[0.2em] opacity-40 ${isDark ? "text-white" : "text-[#1A1F36]"}`}>Current year only</p>
                            ) : goalPacingStats.avgRate === 0 ? (
                              <p className={`text-[10px] font-bold uppercase tracking-[0.2em] opacity-40 ${isDark ? "text-white" : "text-[#1A1F36]"}`}>Add bookings to see this stat</p>
                            ) : (
                              <>
                                <p className={`${secondaryTextClasses} text-xs font-light`}>Billed days needed to reach goal</p>
                                <p className={`text-[10px] font-bold uppercase tracking-[0.15em] mt-0.5 ${goalPacingStats.achievable ? (isDark ? "text-green-400" : "text-green-600") : (isDark ? "text-amber-400" : "text-amber-600")}`}>
                                  {goalPacingStats.achievable ? "Achievable this year" : "Exceeds remaining days"}
                                </p>
                              </>
                            )}
                          </div>
                        </>
                      )}
                    </>
                  )}
                </div>
              </motion.div>

            </div>
          ) : (
            <motion.div
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.5, delay: 0.15 }}
              className={`${cardBgClasses} border ${borderClasses} rounded-[1.5rem] p-8 flex flex-col items-center justify-center gap-3 min-h-[100px]`}>
              <p className={`text-[11px] font-bold uppercase tracking-[0.2em] text-center ${secondaryTextClasses} opacity-60`}>Set your annual goal to unlock goal-based pacing</p>
              <Link to="/home" className="text-[11px] font-bold uppercase tracking-[0.15em] text-[#4A6CF7] hover:opacity-70 transition-opacity">Update Goal →</Link>
            </motion.div>
          )}

          {/* Booking Pace — full width */}
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.4 }}
            className={`${cardBgClasses} border ${borderClasses} rounded-[2.5rem] mt-6 flex flex-col overflow-hidden`}>
            <div className="p-6 md:p-12 pb-6 flex flex-col">
              <div className="flex items-center gap-2 mb-3">
                <p className={`${labelClasses} text-[14px] font-bold uppercase tracking-[0.2em]`}>Yearly Goal Progress</p>
                <Link to="/help?article=annual-booking-pace" title="Yearly Goal Progress, Explained" className={`w-4 h-4 rounded-full flex-shrink-0 flex items-center justify-center text-[9px] font-black transition-all ${isDark ? "bg-white/10 text-white/30 hover:bg-white hover:text-black" : "bg-black/5 text-[#1A1F36]/25 hover:bg-black hover:text-white"}`}>?</Link>
              </div>
              <div className="flex items-baseline gap-6 mb-1">
                {annualGoal > 0 ? (
                  goalPaceDays ? (
                    <span className="text-3xl font-black" style={{ color: goalPaceDays.diff >= 0 ? (isDark ? '#4ade80' : '#15803d') : (isDark ? '#fbbf24' : '#d97706') }}>
                      {Math.abs(goalPaceDays.diff)} <span className={`text-sm font-bold ${secondaryTextClasses}`}>DAYS {goalPaceDays.diff >= 0 ? 'AHEAD OF' : 'BEHIND'} GOAL PACE</span>
                    </span>
                  ) : (
                    <span className={`text-sm font-bold uppercase tracking-[0.1em] opacity-40 ${secondaryTextClasses}`}>Add bookings to see goal pace</span>
                  )
                ) : (
                  <Link to="/home" className="text-sm font-bold uppercase tracking-[0.1em] text-[#4A6CF7] hover:opacity-70 transition-opacity">
                    Set an annual goal to see your pacing →
                  </Link>
                )}
              </div>

              {bookingsLoading ? (
                <div className="flex items-center justify-center mt-4 py-8">
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full border-2 border-[#4A6CF7]/30 border-t-[#4A6CF7] animate-spin" />
                    <span className={`text-[11px] font-bold uppercase tracking-[0.2em] opacity-30 ${isDark ? "text-white" : "text-[#1A1F36]"}`}>Loading</span>
                  </div>
                </div>
              ) : yearBookings.length === 0 ? (
                <div className="flex items-center justify-center mt-4 py-8">
                  <p className={`text-[11px] font-bold uppercase tracking-[0.3em] text-center ${secondaryTextClasses} opacity-50`}>Add bookings to track your progress.</p>
                </div>
              ) : (
                <div className="flex flex-col mt-4">
                  {(() => {
                    const now = new Date();
                    const yr = parseInt(selectedYear);
                    const isCurrentYear = yr === now.getFullYear();
                    const currentMonthIdx = isCurrentYear ? now.getMonth() : -1;
                    const todayMidnight = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                    const overlapDays = (bStart: Date, bEnd: Date, winStart: Date, winEnd: Date): number => {
                      const s = bStart > winStart ? bStart : winStart;
                      const e = bEnd   < winEnd   ? bEnd   : winEnd;
                      return countWeekdaysBetween(s, e);
                    };
                    const rechartData = performanceData.map((d, i) => {
                      const monthStart = new Date(yr, i, 1);
                      const monthEnd   = new Date(yr, i + 1, 0);
                      const availableDays = countBillableDays(monthStart, monthEnd, billableStats.holidays);
                      if (isCurrentYear && i > currentMonthIdx) {
                        const futureDays = yearBookings.reduce((t, b) => {
                          if (!b.startDate || !b.endDate) return t;
                          const scale = hourlyScale(b);
                          return t + overlapDays(new Date(b.startDate + "T00:00:00"), new Date(b.endDate + "T00:00:00"), monthStart, monthEnd) * scale;
                        }, 0);
                        return { month: d.month, monthDays: null as number | null, workedDays: null as number | null, committedDays: null as number | null, futureDays: futureDays > 0 ? futureDays : (null as number | null), availableDays };
                      }
                      if (i === currentMonthIdx) {
                        const tomorrow = new Date(todayMidnight.getFullYear(), todayMidnight.getMonth(), todayMidnight.getDate() + 1);
                        const workedDays = yearBookings.reduce((t, b) => {
                          if (!b.startDate || !b.endDate) return t;
                          return t + overlapDays(new Date(b.startDate + "T00:00:00"), new Date(b.endDate + "T00:00:00"), monthStart, todayMidnight) * hourlyScale(b);
                        }, 0);
                        const committedDays = yearBookings.reduce((t, b) => {
                          if (!b.startDate || !b.endDate) return t;
                          return t + overlapDays(new Date(b.startDate + "T00:00:00"), new Date(b.endDate + "T00:00:00"), tomorrow, monthEnd) * hourlyScale(b);
                        }, 0);
                        const total = workedDays + committedDays;
                        return { month: d.month, monthDays: total, workedDays, committedDays, futureDays: total, availableDays };
                      }
                      const monthDays = yearBookings.reduce((t, b) => {
                        if (!b.startDate || !b.endDate) return t;
                        return t + overlapDays(new Date(b.startDate + "T00:00:00"), new Date(b.endDate + "T00:00:00"), monthStart, monthEnd) * hourlyScale(b);
                      }, 0);
                      return { month: d.month, monthDays, workedDays: monthDays, committedDays: null as number | null, futureDays: null as number | null, availableDays };
                    });
                    const maxBooked = Math.max(0, ...rechartData.filter(d => d.monthDays !== null).map(d => d.monthDays as number));
                    const maxFuture = Math.max(0, ...rechartData.filter(d => d.futureDays !== null).map(d => d.futureDays as number));
                    const yMax = Math.max(30, maxBooked, maxFuture);
                    const lastBilledIdx = rechartData.reduce((acc, d, i) => d.monthDays !== null ? i : acc, -1);
                    const lineColor    = "#4A6CF7";
                    const dotStroke    = isDark ? "#1a1f2e" : "#ffffff";
                    const getDotColor = (monthDays: number | null, availableDays: number) => {
                      if (monthDays === null || availableDays === 0) return "#4A6CF7";
                      const r = monthDays / availableDays;
                      if (r > 1.0)   return "#15803d";
                      if (r >= 0.80) return "#16a34a";
                      if (r >= 0.55) return "#9ca3af";
                      return "#d97706";
                    };
                    const elapsedColor = isDark ? "rgba(255,255,255,0.18)" : "rgba(0,0,0,0.13)";
                    const gridColor    = isDark ? "rgba(255,255,255,0.05)" : "rgba(0,0,0,0.04)";
                    const tickColor    = isDark ? "rgba(255,255,255,0.3)" : "#94a3b8";
                    return (
                      <ResponsiveContainer width="100%" height={220}>
                        <LineChart data={rechartData} margin={{ top: 8, right: 8, left: 5, bottom: 0 }}>
                          <CartesianGrid strokeDasharray="3 3" vertical={false} stroke={gridColor} />
                          <XAxis dataKey="month" axisLine={false} tickLine={false} tick={{ fontSize: 10, fill: tickColor, fontWeight: 600 }} dy={8} interval={0} padding={{ left: 15, right: 5 }} />
                          <YAxis domain={[0, yMax]} axisLine={false} tickLine={false} tick={{ fontSize: 9, fill: tickColor, fontWeight: 600 }} width={28} ticks={[0, 10, 20, 30].filter(t => t <= yMax)} allowDecimals={false} />
                          <Tooltip
                            content={({ active, payload, label }: any) => {
                              if (!active || !payload?.length) return null;
                              const dp = rechartData.find(d => d.month === label);
                              if (!dp || (dp.monthDays === null && dp.futureDays === null)) return null;
                              const fmt = (n: number) => parseFloat(n.toFixed(1));
                              const isCurrentMo = dp.committedDays !== null;
                              const isFutureOnly = dp.monthDays === null && dp.futureDays !== null;
                              const daysLabel = isCurrentMo && (dp.committedDays ?? 0) > 0
                                ? `${fmt(dp.workedDays ?? 0)} done · ${fmt(dp.committedDays ?? 0)} ahead`
                                : isFutureOnly
                                  ? `${fmt(dp.futureDays as number)} days committed`
                                  : `${fmt(dp.monthDays ?? 0)} days booked`;
                              const fillRate = (!isFutureOnly && dp.availableDays > 0 && dp.monthDays !== null)
                                ? Math.round((dp.monthDays / dp.availableDays) * 100) : null;
                              const moStatus: string | null = fillRate === null ? null
                                : fillRate > 100 ? 'ABOVE PACE' : fillRate >= 80 ? 'ON PACE' : fillRate >= 55 ? 'NEUTRAL' : 'BEHIND PACE';
                              const statusColor = moStatus === 'ABOVE PACE' ? '#15803d' : moStatus === 'ON PACE' ? '#16a34a' : moStatus === 'NEUTRAL' ? '#9ca3af' : moStatus === 'BEHIND PACE' ? '#d97706' : '#4A6CF7';
                              return (
                                <div className={`backdrop-blur-md border px-4 py-3 rounded-xl shadow-lg min-w-[180px] ${isDark ? "bg-white/10 border-white/10 text-white" : "bg-white/95 border-slate-100 text-slate-900"}`}>
                                  <span className={`text-[10px] font-bold uppercase tracking-widest ${isDark ? "text-white/40" : "text-slate-400"}`}>{label}</span>
                                  {fillRate !== null && (
                                    <>
                                      <div className="mt-2 mb-1">
                                        <span className="text-base font-black">{fillRate}%</span>
                                        <span className={`text-[10px] font-bold ml-1.5 uppercase tracking-wide ${isDark ? "text-white/40" : "text-slate-400"}`}>filled</span>
                                      </div>
                                      <div className="flex items-center gap-1.5 mb-2.5">
                                        <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: statusColor }} />
                                        <span className="text-[11px] font-bold uppercase tracking-wide" style={{ color: statusColor }}>{moStatus}</span>
                                      </div>
                                    </>
                                  )}
                                  <div className={`text-[11px] pt-2 border-t ${isDark ? "text-white/40 border-white/10" : "text-slate-400 border-slate-100"}`}>{daysLabel} · {dp.availableDays} avail.</div>
                                </div>
                              );
                            }}
                            cursor={{ stroke: gridColor, strokeWidth: 1, strokeDasharray: "4 4" }}
                            isAnimationActive={false}
                          />
                          <Line type="monotone" dataKey="availableDays" stroke={elapsedColor} strokeWidth={2} strokeDasharray="5 5" dot={false} activeDot={false} connectNulls={true} animationDuration={1500} />
                          <Line type="monotone" dataKey="futureDays" stroke={lineColor} strokeWidth={2} strokeDasharray="4 4" strokeOpacity={0.5} dot={false} activeDot={{ r: 5, strokeWidth: 0, fill: lineColor, fillOpacity: 0.5 }} connectNulls={false} animationDuration={1500} />
                          <Line
                            type="monotone" dataKey="monthDays" stroke={lineColor} strokeWidth={2} connectNulls={false}
                            dot={(props: any) => {
                              if (props.value === null || props.value === undefined) return <g key={props.index} />;
                              const isLast = props.index === lastBilledIdx;
                              const d = rechartData[props.index];
                              const dotColor = getDotColor(d?.monthDays ?? null, d?.availableDays ?? 0);
                              return <circle key={props.index} cx={props.cx} cy={props.cy} r={isLast ? 5 : 3} fill={dotColor} stroke={isLast ? dotStroke : "none"} strokeWidth={isLast ? 2 : 0} />;
                            }}
                            activeDot={{ r: 7, strokeWidth: 0, fill: lineColor }}
                            animationDuration={2000}
                          />
                        </LineChart>
                      </ResponsiveContainer>
                    );
                  })()}
                  {(() => {
                    const now2 = new Date();
                    const yr2 = parseInt(selectedYear);
                    const hasFutureBookings = yr2 === now2.getFullYear() && yearBookings.some(b => {
                      if (!b.endDate) return false;
                      return new Date(b.endDate + "T00:00:00") > new Date(now2.getFullYear(), now2.getMonth() + 1, 0);
                    });
                    return (
                      <div className="flex gap-6 mt-4 flex-wrap">
                        <div className="flex items-center gap-2">
                          <div className={`w-5 h-0.5 rounded-full ${isDark ? "bg-blue-400" : "bg-blue-500"}`} />
                          <span className={`text-[10px] font-bold uppercase tracking-widest ${secondaryTextClasses}`}>Days Booked</span>
                        </div>
                        {hasFutureBookings && (
                          <div className="flex items-center gap-2">
                            <div style={{ width: 20, height: 0, borderTop: `2px dashed ${isDark ? 'rgba(96,165,250,0.5)' : 'rgba(37,99,235,0.45)'}` }} />
                            <span className={`text-[10px] font-bold uppercase tracking-widest ${secondaryTextClasses}`}>Committed</span>
                          </div>
                        )}
                        <div className="flex items-center gap-2">
                          <div style={{ width: 20, height: 0, borderTop: `2px dashed ${isDark ? 'rgba(255,255,255,0.25)' : 'rgba(0,0,0,0.2)'}` }} />
                          <span className={`text-[10px] font-bold uppercase tracking-widest ${secondaryTextClasses}`}>Bookable Days</span>
                        </div>
                      </div>
                    );
                  })()}
                </div>
              )}
            </div>

            {/* ── Footer ───────────────────────────────────────────────────── */}
            <div className={`border-t ${borderClasses} ${isDark ? 'bg-white/[0.025]' : 'bg-slate-50/60'}`}>
              <div className={`grid grid-cols-1 sm:grid-cols-2 divide-y sm:divide-y-0 sm:divide-x ${isDark ? 'divide-white/10' : 'divide-slate-100'}`}>
                <div className="p-7">
                  <div className="flex items-center justify-between mb-3">
                    <p className={`text-[10px] font-bold uppercase tracking-[0.2em] ${labelClasses}`}>Against Last Year</p>
                    {bookingsLoading ? (
                      <div className="w-3.5 h-3.5 rounded-full border-2 border-[#4A6CF7]/30 border-t-[#4A6CF7] animate-spin" />
                    ) : vsLastYear !== null && (
                      vsLastYear.diff >= 0
                        ? <span className={`text-[9px] font-black uppercase tracking-wide px-2 py-0.5 rounded-full ${isDark ? "bg-green-500/20 text-green-300" : "bg-green-100 text-green-700"}`}>Ahead</span>
                        : <span className={`text-[9px] font-black uppercase tracking-wide px-2 py-0.5 rounded-full ${isDark ? "bg-orange-500/20 text-orange-300" : "bg-orange-100 text-orange-700"}`}>Behind</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-xl flex-shrink-0 ${isDark ? "bg-blue-500/10" : "bg-blue-50"}`}>
                      <Clock size={15} className={isDark ? "text-blue-400" : "text-blue-500"} />
                    </div>
                    <div>
                      {vsLastYear === null ? (
                        <p className={`text-[10px] font-bold uppercase tracking-[0.2em] ${secondaryTextClasses} opacity-40`}>No prior year data</p>
                      ) : (
                        <>
                          <div className="flex items-center gap-1.5">
                            <span className={`text-base font-black ${isDark ? "text-blue-400" : "text-blue-600"}`}>{sym}{vsLastYear.thisYear.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                            <span className={`text-[10px] font-bold ${secondaryTextClasses}`}>vs</span>
                            <span className={`text-base font-black ${secondaryTextClasses}`}>{sym}{vsLastYear.lastYear.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                          </div>
                          <p className={`text-xs font-light ${secondaryTextClasses} mt-0.5`}>Earned vs same point last year</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
                <div className="p-7">
                  <div className="flex items-center justify-between mb-3">
                    <p className={`text-[10px] font-bold uppercase tracking-[0.2em] ${labelClasses}`}>Earning Target Pace</p>
                    {bookingsLoading ? (
                      <div className="w-3.5 h-3.5 rounded-full border-2 border-[#4A6CF7]/30 border-t-[#4A6CF7] animate-spin" />
                    ) : vsAnnualGoal !== null && (
                      vsAnnualGoal.diff >= 0
                        ? <span className={`text-[9px] font-black uppercase tracking-wide px-2 py-0.5 rounded-full ${isDark ? "bg-green-500/20 text-green-300" : "bg-green-100 text-green-700"}`}>On Track</span>
                        : <span className={`text-[9px] font-black uppercase tracking-wide px-2 py-0.5 rounded-full ${isDark ? "bg-orange-500/20 text-orange-300" : "bg-orange-100 text-orange-700"}`}>Behind</span>
                    )}
                  </div>
                  <div className="flex items-center gap-3">
                    <div className={`p-2 rounded-xl flex-shrink-0 ${isDark ? "bg-blue-500/10" : "bg-blue-50"}`}>
                      <Target size={15} className={isDark ? "text-blue-400" : "text-blue-500"} />
                    </div>
                    <div>
                      {vsAnnualGoal === null ? (
                        <p className={`text-[10px] font-bold uppercase tracking-[0.2em] ${secondaryTextClasses} opacity-40`}>Set an annual goal to compare</p>
                      ) : (
                        <>
                          <div className="flex items-center gap-1.5">
                            <span className={`text-base font-black ${isDark ? "text-blue-400" : "text-blue-600"}`}>{sym}{vsAnnualGoal.actual.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                            <span className={`text-[10px] font-bold ${secondaryTextClasses}`}>vs</span>
                            <span className={`text-base font-black ${secondaryTextClasses}`}>{sym}{vsAnnualGoal.proratedGoal.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                          </div>
                          <p className={`text-xs font-light ${secondaryTextClasses} mt-0.5`}>Earned vs. where you should be</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Row 3 — Tax Reserve */}
        <div className={`border-t ${borderClasses} pt-12`}>
          <p className={`${labelClasses} text-[10px] font-bold uppercase tracking-[0.2em] mb-3`}>Tax Reserve</p>
          <div className="grid grid-cols-1 md:grid-cols-[3fr_4fr] gap-3.5">
            <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5, delay: 0.3 }}
              className={`${cardBgClasses} border ${borderClasses} rounded-[1.5rem] p-6 h-[180px] flex flex-col`}>

              <div className="flex items-center justify-between mb-3">
                <p className={`${labelClasses} text-[14px] font-bold uppercase tracking-[0.2em]`}>This Year's Tax Estimate</p>
                <Link to="/taxes"
                  className={`text-[10px] font-bold px-3 py-1 rounded-xl flex items-center gap-1.5 transition-all hover:scale-105 ${isDark ? "bg-white/10 text-white/50 hover:bg-white/20 hover:text-white" : "bg-black/5 text-[#1A1F36]/50 hover:bg-black/10 hover:text-[#1A1F36]"}`}>
                  <svg width={10} height={10} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M17 3a2.828 2.828 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5L17 3z" />
                  </svg>
                  Edit
                </Link>
              </div>

              <div className="flex-1 flex flex-col justify-end">
                <p className={`text-[34px] font-extrabold leading-none ${isDark ? "text-purple-400" : "text-[#A259FF]"}`}>
                  {reserveDisplay}
                </p>
                <p className={`${secondaryTextClasses} text-[11px] mt-1`}>Reserve on {ytdDisplay} booked this year</p>
                <div className="flex items-center gap-3 mt-3">
                  <input type="range" min={1} max={50} value={taxReservePct}
                    onChange={e => setTaxReservePct(Number(e.target.value))}
                    className="flex-1 accent-[#A259FF] cursor-pointer" />
                  <span className={`text-[11px] font-bold ${isDark ? "text-purple-400" : "text-[#A259FF]"}`}>{taxReservePct}%</span>
                </div>
                <p className="text-[10px] font-bold uppercase tracking-[0.2em] opacity-30 mt-1">
                  Adjust to explore · Resets to your tax estimate on refresh
                </p>
              </div>
            </motion.div>

            <motion.div initial={{ opacity: 0, scale: 0.98 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.35 }}
              className="rounded-[1.5rem] overflow-hidden h-[180px]">
              <img src="https://res.cloudinary.com/dlmbskxuc/image/upload/v1772829613/FH_stats_avmuic.png" alt="" className="w-full h-full object-cover" />
            </motion.div>
          </div>
        </div>


        {/* Row 5 — Clients */}
        <div className={`border-t ${borderClasses} pt-12 pb-12`}>
          <p className={`${labelClasses} text-[10px] font-bold uppercase tracking-[0.2em] mb-3`}>Clients</p>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 w-full">

            {/* Most Valuable Clients — dynamic top 3 */}
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.45 }}
              className={`${cardBgClasses} border ${borderClasses} rounded-[2.5rem] p-6 md:p-12 min-h-[440px] flex flex-col`}>
              <div className="flex items-center justify-between mb-3">
                <h3 className={`text-[14px] font-bold uppercase tracking-[0.2em] ${labelClasses}`}>Most Valuable Clients</h3>
                <div className="relative">
                  <button onClick={() => setClientsYearDropdownOpen(v => !v)}
                    className={`text-xs font-black uppercase tracking-widest transition-all ${isDark ? "text-white/30 hover:text-white/60" : "text-[#1A1F36]/30 hover:text-[#1A1F36]/60"}`}>
                    {clientsYear} ▾
                  </button>
                  {clientsYearDropdownOpen && (
                    <div className={`absolute right-0 top-7 z-50 rounded-2xl border shadow-xl overflow-hidden ${isDark ? "bg-[#1a1f2e] border-white/10" : "bg-white border-gray-200"}`}>
                      {["OVERALL", ...statYears].map(yr => (
                        <button key={yr} onClick={() => { setClientsYear(yr); setClientsYearDropdownOpen(false); }}
                          className={`block w-full text-left px-6 py-3 text-xs font-black uppercase tracking-widest transition-all ${
                            clientsYear === yr ? "text-[#4A6CF7]"
                            : isDark ? "text-white/40 hover:text-white hover:bg-white/5"
                            : "text-[#1A1F36]/40 hover:text-[#1A1F36] hover:bg-gray-50"
                          }`}>{yr}</button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              <div className="flex-1 flex items-center justify-center overflow-hidden">
                {bookingsLoading ? (
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full border-2 border-[#4A6CF7]/30 border-t-[#4A6CF7] animate-spin" />
                    <span className={`text-[11px] font-bold uppercase tracking-[0.2em] opacity-30 ${isDark ? "text-white" : "text-[#1A1F36]"}`}>Loading</span>
                  </div>
                ) : topClients.length === 0 ? (
                  <p className={`text-[11px] font-bold uppercase tracking-[0.3em] text-center ${secondaryTextClasses} opacity-50`}>
                    {clientsYear === "OVERALL" ? "No client data yet." : `No client data for ${clientsYear} yet.`}
                  </p>
                ) : (
                  <svg viewBox="35 30 320 200" width="100%" height="100%" preserveAspectRatio="xMidYMid meet" style={{ maxHeight: 420, display: 'block', margin: '0 auto' }}>
                    {topClients.map((client, idx) => {
                      const layout = bubbleLayout[idx];
                      if (!layout) return null;
                      return (
                        <motion.g key={client.name} whileHover={{ scale: 1.05 }}
                          style={{ originX: `${layout.x + layout.w / 2}px`, originY: `${layout.y + layout.h / 2}px` }} className="cursor-pointer">
                          <rect x={layout.x} y={layout.y} width={layout.w} height={layout.h} rx={layout.r} fill={layout.color} className="transition-all duration-300" />
                          <text x={layout.tx} y={layout.ty} textAnchor={layout.ta} fill={isDark ? "#ffffff" : "#0f1419"} fontSize={client.name.length > 18 ? "7" : client.name.length > 12 ? "8.5" : "10"} fontWeight="800" letterSpacing="0.05em">
                            {client.name.toUpperCase()}
                          </text>
                          <text x={layout.ptx} y={layout.ty + 12} textAnchor={layout.pta} fill="#4A6CF7" fontSize="9" fontWeight="900">{client.pct}</text>
                        </motion.g>
                      );
                    })}
                  </svg>
                )}
              </div>
            </motion.div>

            {/* Avg Booking Length by Client */}
            <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.5, delay: 0.5 }}
              className={`${cardBgClasses} border ${borderClasses} rounded-[2.5rem] p-6 md:p-12 flex flex-col`}>
              <div className="flex items-center justify-between mb-8">
                <h3 className={`text-[14px] font-bold uppercase tracking-[0.2em] ${labelClasses}`}>Avg Booking Length by Client</h3>
                <div className="relative">
                  <button onClick={() => setAvgBookingYearDropdownOpen(v => !v)}
                    className={`text-xs font-black uppercase tracking-widest transition-all ${isDark ? "text-white/30 hover:text-white/60" : "text-[#1A1F36]/30 hover:text-[#1A1F36]/60"}`}>
                    {avgBookingYear} ▾
                  </button>
                  {avgBookingYearDropdownOpen && (
                    <div className={`absolute right-0 top-7 z-50 rounded-2xl border shadow-xl overflow-hidden ${isDark ? "bg-[#1a1f2e] border-white/10" : "bg-white border-gray-200"}`}>
                      {["OVERALL", ...statYears].map(yr => (
                        <button key={yr} onClick={() => { setAvgBookingYear(yr); setAvgBookingYearDropdownOpen(false); }}
                          className={`block w-full text-left px-6 py-3 text-xs font-black uppercase tracking-widest transition-all ${
                            avgBookingYear === yr ? "text-[#4A6CF7]"
                            : isDark ? "text-white/40 hover:text-white hover:bg-white/5"
                            : "text-[#1A1F36]/40 hover:text-[#1A1F36] hover:bg-gray-50"
                          }`}>{yr}</button>
                      ))}
                    </div>
                  )}
                </div>
              </div>
              {bookingsLoading ? (
                <div className="flex-1 flex items-center justify-center">
                  <div className="flex items-center gap-3">
                    <div className="w-6 h-6 rounded-full border-2 border-[#4A6CF7]/30 border-t-[#4A6CF7] animate-spin" />
                    <span className={`text-[11px] font-bold uppercase tracking-[0.2em] opacity-30 ${isDark ? "text-white" : "text-[#1A1F36]"}`}>Loading</span>
                  </div>
                </div>
              ) : avgBookingData.length === 0 ? (
                <div className="flex-1 flex items-center justify-center">
                  <p className={`text-[11px] font-bold uppercase tracking-[0.3em] text-center ${secondaryTextClasses} opacity-50`}>
                    {avgBookingYear === "OVERALL" ? "No booking data yet." : `No booking data for ${avgBookingYear} yet.`}
                  </p>
                </div>
              ) : (
                <div className="flex flex-col gap-6 flex-1 justify-center">
                  {avgBookingData.map((d, idx) => (
                    <div key={d.client}>
                      <div className="flex justify-between mb-2">
                        <span className={`text-sm font-bold ${textClasses}`}>{d.client}</span>
                        <span className="text-sm font-black text-[#4A6CF7]">{d.days} days</span>
                      </div>
                      <div className="h-2 w-full bg-gray-100 rounded-full overflow-hidden">
                        <motion.div initial={{ width: 0 }} animate={{ width: `${(d.days / maxAvgDays) * 100}%` }}
                          transition={{ duration: 1, delay: 0.5 + idx * 0.1 }}
                          className="h-full rounded-full" style={{ backgroundColor: d.color }} />
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </motion.div>
          </div>
        </div>

      </main>
    </div>
  );
}

// ─── Icon components ──────────────────────────────────────────────────────────

function SunIcon() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <circle cx="12" cy="12" r="5" />
      <line x1="12" y1="1" x2="12" y2="3" /><line x1="12" y1="21" x2="12" y2="23" />
      <line x1="4.22" y1="4.22" x2="5.64" y2="5.64" /><line x1="18.36" y1="18.36" x2="19.78" y2="19.78" />
      <line x1="1" y1="12" x2="3" y2="12" /><line x1="21" y1="12" x2="23" y2="12" />
      <line x1="4.22" y1="19.78" x2="5.64" y2="18.36" /><line x1="18.36" y1="5.64" x2="19.78" y2="4.22" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width={20} height={20} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}
